import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BookingsKanban from "@/components/BookingsKanban";
import LocationThumb from "@/components/LocationThumb";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar,
  FileText,
  Users,
  MessageSquare,
  LogOut,
  RefreshCw,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Building2,
  Home,
  Search,
  Trash2,
  Eye,
  Download,
  MapPin,
  ArrowRightLeft,
  Mail,
  Send,
  Image as ImageIcon,
  Upload,
  Star,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  XCircle,
  Wrench,
  Users2,
  LayoutGrid,
  List as ListIcon,
  Phone as PhoneIcon,
  HardHat,
  Tag,
} from "lucide-react";
import { InvoiceTemplate } from "@/components/InvoiceTemplate";
import { PromoCodesTab } from "@/components/admin/PromoCodesTab";
import { ManualRegistryDialog } from "@/components/admin/ManualRegistryDialog";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState("all"); // "all" | "pending" | "paid"

  // Portfolio state
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioCategory, setPortfolioCategory] = useState("roofing");
  const [portfolioBefore, setPortfolioBefore] = useState(null);
  const [portfolioAfter, setPortfolioAfter] = useState(null);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);

  // Quote requests + Reviews
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [reviews, setReviews] = useState([]);

  // Crew management
  const [crew, setCrew] = useState([]);
  const [crewForm, setCrewForm] = useState({
    name: "",
    phone: "",
    email: "",
    specialties: "",
    bio: "",
    is_active: true,
  });
  const [editingCrewId, setEditingCrewId] = useState(null);
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [savingCrew, setSavingCrew] = useState(false);

  // Bookings view mode
  const [bookingsView, setBookingsView] = useState("table"); // 'table' | 'kanban'

  // Labour Registry
  const [registryApps, setRegistryApps] = useState([]);

  // Mark-as-paid dialog
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);
  const [markPaidForm, setMarkPaidForm] = useState({
    payment_method: "cash",
    amount: "",
    reference: "",
    notes: "",
  });
  const [savingMarkPaid, setSavingMarkPaid] = useState(false);

  // Crew Payouts
  const [crewPayouts, setCrewPayouts] = useState([]);
  const [outstandingPayouts, setOutstandingPayouts] = useState([]); // groups[]
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    crew_id: "",
    invoice_numbers: [],
    amount: "",
    payment_method: "cash",
    reference: "",
    notes: "",
    paid_at: new Date().toISOString().slice(0, 10),
  });
  const [savingPayout, setSavingPayout] = useState(false);
  
  // Invoice creation state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    user_id: "",
    booking_id: "",
    description: "",
    amount: "",
    due_date: "",
    document_type: "invoice",
    notes: "",
    discount_type: "none",
    discount_value: "",
    line_items: [{ description: "", quantity: 1, unit_price: "" }],
    // Manual customer fields for non-registered customers
    manual_customer: false,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
  });
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  
  // Invoice preview state
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const templateRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statsRes, bookingsRes, invoicesRes, usersRes, contactsRes, portfolioRes, quotesRes, reviewsRes, crewRes, payoutsRes, outstandingRes, registryRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/invoices`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/portfolio`),
        axios.get(`${API}/admin/quotes`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/reviews`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/crew`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/crew-payouts`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/crew-payouts/outstanding`).catch(() => ({ data: { groups: [] } })),
        axios.get(`${API}/admin/labour-registry`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setInvoices(invoicesRes.data);
      setUsers(usersRes.data);
      setContacts(contactsRes.data);
      setPortfolio(portfolioRes.data || []);
      setQuoteRequests(quotesRes.data || []);
      setReviews(reviewsRes.data || []);
      setCrew(crewRes.data || []);
      setCrewPayouts(payoutsRes.data || []);
      setOutstandingPayouts(outstandingRes.data?.groups || []);
      setRegistryApps(registryRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const uploadPortfolioProject = async () => {
    if (!portfolioBefore || !portfolioAfter) {
      toast.error("Please select both a Before and an After image");
      return;
    }
    if (!portfolioCategory) {
      toast.error("Please select a service category");
      return;
    }
    setUploadingPortfolio(true);
    try {
      const formData = new FormData();
      formData.append("service_category", portfolioCategory);
      formData.append("before_image", portfolioBefore);
      formData.append("after_image", portfolioAfter);
      const res = await axios.post(`${API}/admin/portfolio`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Portfolio project added");
      setPortfolio((prev) => [res.data, ...prev]);
      setPortfolioBefore(null);
      setPortfolioAfter(null);
    } catch (error) {
      console.error("Error uploading portfolio:", error);
      toast.error(error.response?.data?.detail || "Failed to upload portfolio");
    } finally {
      setUploadingPortfolio(false);
    }
  };

  const deletePortfolioProject = async (projectId) => {
    if (!window.confirm("Remove this portfolio project? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/portfolio/${projectId}`);
      setPortfolio((prev) => prev.filter((p) => p.id !== projectId));
      toast.success("Portfolio project removed");
    } catch (error) {
      console.error("Error deleting portfolio:", error);
      toast.error("Failed to delete project");
    }
  };

  const togglePortfolioFeatured = async (projectId) => {
    try {
      const res = await axios.put(`${API}/admin/portfolio/${projectId}/featured`);
      setPortfolio((prev) =>
        prev.map((p) => (p.id === projectId ? res.data : p))
      );
      toast.success(
        res.data.is_featured ? "Project featured on homepage" : "Removed from featured"
      );
    } catch (error) {
      console.error("Error toggling featured:", error);
      toast.error("Failed to update featured status");
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}/status`, { status });
      setQuoteRequests((prev) =>
        prev.map((q) => (q.id === quoteId ? { ...q, status } : q))
      );
      toast.success("Quote status updated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update quote");
    }
  };

  const approveReview = async (reviewId) => {
    try {
      const res = await axios.put(`${API}/admin/reviews/${reviewId}/approve`);
      setReviews((prev) => prev.map((r) => (r.id === reviewId ? res.data : r)));
      toast.success("Review approved");
    } catch (error) {
      toast.error("Failed to approve review");
    }
  };

  const rejectReview = async (reviewId) => {
    try {
      const res = await axios.put(`${API}/admin/reviews/${reviewId}/reject`);
      setReviews((prev) => prev.map((r) => (r.id === reviewId ? res.data : r)));
      toast.success("Review rejected");
    } catch (error) {
      toast.error("Failed to reject review");
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      toast.success("Review deleted");
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Delete this booking permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      setBookings((prev) => prev.filter((b) => b.id !== bookingId));
      toast.success("Booking deleted");
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const deleteQuoteRequest = async (quoteId) => {
    if (!window.confirm("Delete this quote request permanently?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`);
      setQuoteRequests((prev) => prev.filter((q) => q.id !== quoteId));
      toast.success("Quote request deleted");
    } catch (error) {
      toast.error("Failed to delete quote request");
    }
  };

  const deleteInvoiceDoc = async (invoiceNumber) => {
    if (!window.confirm("Delete this document permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/invoices/${invoiceNumber}`);
      setInvoices((prev) => prev.filter((i) => i.invoice_number !== invoiceNumber));
      toast.success("Document deleted");
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}`);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      toast.success("Inquiry deleted");
    } catch (error) {
      toast.error("Failed to delete inquiry");
    }
  };

  const deleteCustomer = async (user) => {
    if (!window.confirm(`Delete ${user.name || "this customer"} and ALL their bookings, quotes, invoices and reviews? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${user.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success("Customer and their records deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };


  const deleteRegistryApp = async (appId) => {
    if (!window.confirm("Delete this registry application permanently?")) return;    try {
      await axios.delete(`${API}/admin/labour-registry/${appId}`);
      setRegistryApps((prev) => prev.filter((a) => a.id !== appId));
      toast.success("Application deleted");
    } catch (error) {
      toast.error("Failed to delete application");
    }
  };

  // Crew CRUD
  const openCrewDialog = (existing = null) => {
    if (existing) {
      setEditingCrewId(existing.id);
      setCrewForm({
        name: existing.name || "",
        phone: existing.phone || "",
        email: existing.email || "",
        specialties: (existing.specialties || []).join(", "),
        bio: existing.bio || "",
        is_active: existing.is_active !== false,
      });
    } else {
      setEditingCrewId(null);
      setCrewForm({ name: "", phone: "", email: "", specialties: "", bio: "", is_active: true });
    }
    setShowCrewDialog(true);
  };

  const saveCrew = async () => {
    if (!crewForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingCrew(true);
    const payload = {
      name: crewForm.name.trim(),
      phone: crewForm.phone.trim() || null,
      email: crewForm.email.trim() || null,
      specialties: crewForm.specialties
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      bio: crewForm.bio.trim() || null,
      is_active: crewForm.is_active,
    };
    try {
      if (editingCrewId) {
        const res = await axios.put(`${API}/admin/crew/${editingCrewId}`, payload);
        setCrew((prev) => prev.map((c) => (c.id === editingCrewId ? res.data : c)));
        toast.success("Crew member updated");
      } else {
        const res = await axios.post(`${API}/admin/crew`, payload);
        setCrew((prev) => [res.data, ...prev]);
        toast.success("Crew member added");
      }
      setShowCrewDialog(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save crew member");
    } finally {
      setSavingCrew(false);
    }
  };

  const deleteCrew = async (crewId) => {
    if (!window.confirm("Remove this crew member? They'll be unassigned from any bookings.")) return;
    try {
      await axios.delete(`${API}/admin/crew/${crewId}`);
      setCrew((prev) => prev.filter((c) => c.id !== crewId));
      // Refresh bookings to reflect unassignments
      fetchAllData();
      toast.success("Crew member removed");
    } catch (error) {
      toast.error("Failed to remove crew member");
    }
  };

  const assignCrew = async (bookingId, crewId) => {
    try {
      const res = await axios.put(`${API}/admin/bookings/${bookingId}/assign-crew`, {
        crew_id: crewId || null,
      });
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? res.data : b)));
      toast.success(crewId ? "Crew assigned" : "Crew unassigned");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update assignment");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    delete axios.defaults.headers.common["Authorization"];
    navigate("/admin");
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status: newStatus });
      toast.success("Booking status updated");
      fetchAllData();
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Failed to update booking status");
    }
  };

  const startQuoteForBooking = (booking) => {
    // Pre-fill the invoice/quote dialog from a custom service request booking
    const customer = users.find((u) => u.id === booking.user_id);
    const desc = booking.custom_service_description || booking.notes || "Custom service request";
    setInvoiceForm({
      user_id: booking.user_id || "",
      booking_id: booking.id || "",
      description: `Custom request — ${desc.slice(0, 120)}`,
      amount: "",
      due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      document_type: "quote",
      notes: `Original customer request:\n${desc}`,
      discount_type: "none",
      discount_value: "",
      line_items: [{ description: desc.slice(0, 120), quantity: 1, unit_price: "" }],
      manual_customer: !booking.user_id,
      customer_name: customer?.name || booking.customer_name || "",
      customer_email: customer?.email || booking.customer_email || "",
      customer_phone: customer?.phone || booking.customer_phone || "",
      customer_address: customer?.address || booking.address || "",
    });
    setShowInvoiceDialog(true);
  };

  const createInvoice = async () => {
    // Validate based on whether it's manual customer or registered user
    if (invoiceForm.manual_customer) {
      if (!invoiceForm.customer_name || !invoiceForm.customer_email || !invoiceForm.description || !invoiceForm.due_date) {
        toast.error("Please fill in customer name, email, description, and due date");
        return;
      }
    } else {
      if (!invoiceForm.user_id || !invoiceForm.description || !invoiceForm.due_date) {
        toast.error("Please fill in all required fields");
        return;
      }
    }

    // Validate line items
    const validLineItems = invoiceForm.line_items.filter(
      item => item.description && item.unit_price
    );
    
    if (validLineItems.length === 0 && !invoiceForm.amount) {
      toast.error("Please add at least one line item or enter a total amount");
      return;
    }

    setCreatingInvoice(true);
    try {
      const payload = {
        user_id: invoiceForm.manual_customer ? null : invoiceForm.user_id,
        booking_id: invoiceForm.booking_id || null,
        description: invoiceForm.description,
        amount: parseFloat(invoiceForm.amount) || 0,
        due_date: invoiceForm.due_date,
        document_type: invoiceForm.document_type,
        notes: invoiceForm.notes || null,
        discount_type: invoiceForm.discount_type !== "none" ? invoiceForm.discount_type : null,
        discount_value: invoiceForm.discount_type !== "none" ? (parseFloat(invoiceForm.discount_value) || 0) : 0,
        line_items: validLineItems.length > 0 ? validLineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0)
        })) : null,
        // Manual customer fields
        manual_customer_name: invoiceForm.manual_customer ? invoiceForm.customer_name : null,
        manual_customer_email: invoiceForm.manual_customer ? invoiceForm.customer_email : null,
        manual_customer_phone: invoiceForm.manual_customer ? invoiceForm.customer_phone : null,
        manual_customer_address: invoiceForm.manual_customer ? invoiceForm.customer_address : null,
      };
      
      const response = await axios.post(`${API}/admin/invoices`, payload);
      const docType = invoiceForm.document_type === "quote" ? "Quote" : "Invoice";
      toast.success(`${docType} ${response.data.invoice_number} created!`);
      setShowInvoiceDialog(false);
      setInvoiceForm({
        user_id: "",
        booking_id: "",
        description: "",
        amount: "",
        due_date: "",
        document_type: "invoice",
        notes: "",
        discount_type: "none",
        discount_value: "",
        line_items: [{ description: "", quantity: 1, unit_price: "" }],
        manual_customer: false,
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
      });
      fetchAllData();
    } catch (error) {
      console.error("Error creating document:", error);
      toast.error("Failed to create document");
    } finally {
      setCreatingInvoice(false);
    }
  };

  const addLineItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      line_items: [...invoiceForm.line_items, { description: "", quantity: 1, unit_price: "" }]
    });
  };

  const removeLineItem = (index) => {
    const newItems = invoiceForm.line_items.filter((_, i) => i !== index);
    setInvoiceForm({
      ...invoiceForm,
      line_items: newItems.length > 0 ? newItems : [{ description: "", quantity: 1, unit_price: "" }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...invoiceForm.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoiceForm({ ...invoiceForm, line_items: newItems });
  };

  const calculateTotal = () => {
    return invoiceForm.line_items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const handleViewInvoice = (invoice) => {
    setPreviewInvoice(invoice);
    setShowPreviewDialog(true);
  };

  const handleDownloadPDF = async () => {
    if (!templateRef.current || !previewInvoice) return;
    
    setDownloadingPdf(true);
    try {
      const canvas = await html2canvas(templateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth * ratio, imgHeight * ratio);
      
      const docType = previewInvoice.document_type === "quote" ? "Quote" : "Invoice";
      pdf.save(`ProToCall_${docType}_${previewInvoice.invoice_number}.pdf`);
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleConvertToInvoice = async (invoiceNumber) => {
    try {
      await axios.post(`${API}/admin/invoices/${invoiceNumber}/convert`);
      toast.success("Quote converted to invoice!");
      fetchAllData();
      setShowPreviewDialog(false);
    } catch (error) {
      console.error("Error converting quote:", error);
      toast.error("Failed to convert quote");
    }
  };

  const handleSendEmail = async (invoiceNumber) => {
    try {
      const response = await axios.post(`${API}/admin/invoices/${invoiceNumber}/send-email`);
      toast.success(response.data.message || "Email sent successfully!");
      fetchAllData();
    } catch (error) {
      console.error("Error sending email:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send email";
      toast.error(errorMsg);
    }
  };

  const openMarkPaidDialog = (invoice) => {
    const outstanding = (invoice.amount || 0) - (invoice.amount_paid || 0);
    setMarkPaidInvoice(invoice);
    setMarkPaidForm({
      payment_method: "cash",
      amount: outstanding > 0 ? outstanding.toFixed(2) : "",
      reference: "",
      notes: "",
    });
  };

  const submitMarkPaid = async () => {
    if (!markPaidInvoice) return;
    const parsedAmount = parseFloat(markPaidForm.amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    setSavingMarkPaid(true);
    try {
      await axios.post(
        `${API}/admin/invoices/${markPaidInvoice.invoice_number}/mark-paid`,
        {
          payment_method: markPaidForm.payment_method,
          amount: parsedAmount,
          reference: markPaidForm.reference || null,
          notes: markPaidForm.notes || null,
        }
      );
      toast.success(`Payment recorded for ${markPaidInvoice.invoice_number}`);
      setMarkPaidInvoice(null);
      fetchAllData();
    } catch (error) {
      console.error("Mark paid failed:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSavingMarkPaid(false);
    }
  };

  // Set of invoice numbers that have already been paid out to crew (so we can show status)
  const paidOutInvoiceSet = (() => {
    const s = new Set();
    crewPayouts.forEach((p) => (p.invoice_numbers || []).forEach((n) => s.add(n)));
    return s;
  })();

  const openPayoutDialog = (preselectInvoice = null) => {
    let crew_id = "";
    let invoice_numbers = [];
    if (preselectInvoice) {
      const linkedBooking = bookings.find((b) => b.id === preselectInvoice.booking_id);
      crew_id = linkedBooking?.crew_id || "";
      invoice_numbers = [preselectInvoice.invoice_number];
    }
    setPayoutForm({
      crew_id,
      invoice_numbers,
      amount: "",
      payment_method: "cash",
      reference: "",
      notes: "",
      paid_at: new Date().toISOString().slice(0, 10),
    });
    setShowPayoutDialog(true);
  };

  const submitPayout = async () => {
    if (!payoutForm.crew_id) {
      toast.error("Select a crew member");
      return;
    }
    const amt = parseFloat(payoutForm.amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Enter a valid payout amount");
      return;
    }
    setSavingPayout(true);
    try {
      await axios.post(`${API}/admin/crew-payouts`, {
        crew_id: payoutForm.crew_id,
        invoice_numbers: payoutForm.invoice_numbers,
        amount: amt,
        payment_method: payoutForm.payment_method,
        reference: payoutForm.reference || null,
        notes: payoutForm.notes || null,
        paid_at: payoutForm.paid_at
          ? new Date(payoutForm.paid_at).toISOString()
          : null,
      });
      toast.success("Crew payout recorded");
      setShowPayoutDialog(false);
      fetchAllData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to record payout");
    } finally {
      setSavingPayout(false);
    }
  };

  const deletePayout = async (payoutId) => {
    if (!window.confirm("Reverse this payout? This is for corrections only.")) return;
    try {
      await axios.delete(`${API}/admin/crew-payouts/${payoutId}`);
      toast.success("Payout reversed");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to reverse payout");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
      case "completed":
        return "bg-green-100 text-green-700";
      case "pending":
      case "new":
        return "bg-yellow-100 text-yellow-700";
      case "confirmed":
      case "in_progress":
        return "bg-blue-100 text-blue-700";
      case "cancelled":
      case "overdue":
        return "bg-red-100 text-red-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const filteredBookings = bookings.filter(b => 
    b.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/protocall-logo.png" 
              alt="ProToCall" 
              className="h-10 w-auto"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <div>
              <h1 className="font-bold text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                Admin Dashboard
              </h1>
              <p className="text-slate-400 text-xs">ProToCall Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchAllData}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_bookings || 0}</p>
                  <p className="text-xs text-slate-600">Total Bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pending_bookings || 0}</p>
                  <p className="text-xs text-slate-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_invoices || 0}</p>
                  <p className="text-xs text-slate-600">Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.unpaid_invoices || 0}</p>
                  <p className="text-xs text-slate-600">Unpaid</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                  <p className="text-xs text-slate-600">Customers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.new_contacts || 0}</p>
                  <p className="text-xs text-slate-600">New Inquiries</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="bookings" className="w-full">
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-white border border-slate-200 lg:[&>button]:px-2 lg:[&_svg]:mr-1">
              <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
                <Calendar className="w-4 h-4 mr-2" />
                Bookings
              </TabsTrigger>
              <TabsTrigger value="custom-requests" data-testid="admin-tab-custom-requests">
                <Wrench className="w-4 h-4 mr-2" />
                Custom Requests
                {bookings.filter((b) => b.service_id === "other_custom" && b.status === "pending").length > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white">
                    {bookings.filter((b) => b.service_id === "other_custom" && b.status === "pending").length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="invoices" data-testid="admin-tab-invoices">
                <FileText className="w-4 h-4 mr-2" />
                Invoices
              </TabsTrigger>
              <TabsTrigger value="customers" data-testid="admin-tab-customers">
                <Users className="w-4 h-4 mr-2" />
                Customers
              </TabsTrigger>
              <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries">
                <MessageSquare className="w-4 h-4 mr-2" />
                Inquiries
              </TabsTrigger>
              <TabsTrigger value="portfolio" data-testid="admin-tab-portfolio">
                <ImageIcon className="w-4 h-4 mr-2" />
                Portfolio
              </TabsTrigger>
              <TabsTrigger value="quotes" data-testid="admin-tab-quotes">
                <Sparkles className="w-4 h-4 mr-2" />
                Quotes
                {quoteRequests.filter((q) => q.status === "new").length > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white">
                    {quoteRequests.filter((q) => q.status === "new").length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="admin-tab-reviews">
                <Star className="w-4 h-4 mr-2" />
                Reviews
                {reviews.filter((r) => !r.is_approved).length > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white">
                    {reviews.filter((r) => !r.is_approved).length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="crew" data-testid="admin-tab-crew">
                <Users2 className="w-4 h-4 mr-2" />
                Crew
                {outstandingPayouts.reduce((acc, g) => acc + g.invoices.length, 0) > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white">
                    {outstandingPayouts.reduce((acc, g) => acc + g.invoices.length, 0)}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="registry" data-testid="admin-tab-registry">
                <HardHat className="w-4 h-4 mr-2" />
                Registry
                {registryApps.length > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white">{registryApps.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="promos" data-testid="admin-tab-promos">
                <Tag className="w-4 h-4 mr-2" />
                Promo Codes
              </TabsTrigger>
            </TabsList>
            
            {/* Create Invoice Button */}
            <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Quote/Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New {invoiceForm.document_type === "quote" ? "Quote" : "Invoice"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Document Type */}
                  <div>
                    <Label>Document Type *</Label>
                    <Select
                      value={invoiceForm.document_type}
                      onValueChange={(val) => setInvoiceForm({ ...invoiceForm, document_type: val })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="invoice">Invoice</SelectItem>
                        <SelectItem value="quote">Quote</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Customer Type Toggle */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="manual_customer"
                      checked={invoiceForm.manual_customer}
                      onChange={(e) => setInvoiceForm({ 
                        ...invoiceForm, 
                        manual_customer: e.target.checked,
                        user_id: e.target.checked ? "" : invoiceForm.user_id
                      })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label htmlFor="manual_customer" className="cursor-pointer text-sm">
                      Manual entry (customer without account)
                    </Label>
                  </div>
                  
                  {/* Registered Customer Selection */}
                  {!invoiceForm.manual_customer && (
                    <>
                      <div>
                        <Label>Customer *</Label>
                        <Select
                          value={invoiceForm.user_id}
                          onValueChange={(val) => setInvoiceForm({ ...invoiceForm, user_id: val })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.name} ({user.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Related Booking */}
                      <div>
                        <Label>Related Booking (Optional)</Label>
                        <Select
                          value={invoiceForm.booking_id}
                          onValueChange={(val) => setInvoiceForm({ ...invoiceForm, booking_id: val === "none" ? "" : val })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select booking" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No booking</SelectItem>
                            {bookings.filter(b => b.user_id === invoiceForm.user_id).map((booking) => (
                              <SelectItem key={booking.id} value={booking.id}>
                                {booking.service_name} - {booking.preferred_date}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  
                  {/* Manual Customer Fields */}
                  {invoiceForm.manual_customer && (
                    <div className="space-y-3 p-4 border border-slate-200 rounded-lg bg-white">
                      <p className="text-sm font-medium text-slate-700">Customer Details</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Name *</Label>
                          <Input
                            value={invoiceForm.customer_name}
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_name: e.target.value })}
                            placeholder="Customer name"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={invoiceForm.customer_email}
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_email: e.target.value })}
                            placeholder="customer@email.com"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={invoiceForm.customer_phone}
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_phone: e.target.value })}
                            placeholder="+1 758 000 0000"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input
                            value={invoiceForm.customer_address}
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_address: e.target.value })}
                            placeholder="Street, City"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Description */}
                  <div>
                    <Label>Description/Title *</Label>
                    <Input
                      value={invoiceForm.description}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                      placeholder="e.g., Roof repair service"
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Line Items */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Line Items</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                        <Plus className="w-3 h-3 mr-1" /> Add Item
                      </Button>
                    </div>
                    <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                      {invoiceForm.line_items.map((item, index) => (
                        <div key={index} className="flex gap-2 items-start">
                          <Input
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateLineItem(index, "description", e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, "quantity", e.target.value)}
                            className="w-20"
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, "unit_price", e.target.value)}
                            className="w-28"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {calculateTotal() > 0 && (
                        <div className="flex justify-end pt-2 border-t">
                          <span className="font-semibold">
                            Subtotal: XCD ${calculateTotal().toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Discount (returning customer) */}
                  <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="invoice-discount-section">
                    <Label className="text-sm font-semibold">Discount (Optional)</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Select
                        value={invoiceForm.discount_type}
                        onValueChange={(v) => setInvoiceForm({ ...invoiceForm, discount_type: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid="invoice-discount-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No discount</SelectItem>
                          <SelectItem value="percent">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed (XCD $)</SelectItem>
                        </SelectContent>
                      </Select>
                      {invoiceForm.discount_type !== "none" && (
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={invoiceForm.discount_value}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, discount_value: e.target.value })}
                          placeholder={invoiceForm.discount_type === "percent" ? "e.g. 10" : "e.g. 50"}
                          className="mt-1"
                          data-testid="invoice-discount-value"
                        />
                      )}
                    </div>
                    {invoiceForm.discount_type !== "none" && (() => {
                      const sub = calculateTotal() || parseFloat(invoiceForm.amount) || 0;
                      const val = parseFloat(invoiceForm.discount_value) || 0;
                      const disc = Math.min(invoiceForm.discount_type === "percent" ? sub * val / 100 : val, sub);
                      return (
                        <div className="flex justify-between text-sm pt-2 border-t" data-testid="invoice-discount-summary">
                          <span className="text-slate-600">Discount: −XCD ${disc.toFixed(2)}</span>
                          <span className="font-bold text-slate-900">Total: XCD ${(sub - disc).toFixed(2)}</span>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Due Date */}
                  <div>
                    <Label>{invoiceForm.document_type === "quote" ? "Valid Until" : "Due Date"} *</Label>
                    <Input
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Notes */}
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={invoiceForm.notes}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                      placeholder="Additional notes or terms..."
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                  
                  <Button
                    onClick={createInvoice}
                    disabled={creatingInvoice}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    {creatingInvoice ? "Creating..." : `Create ${invoiceForm.document_type === "quote" ? "Quote" : "Invoice"}`}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="bg-white">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <CardTitle>Service Bookings</CardTitle>
                  <div className="flex items-center gap-3">
                    {/* View toggle */}
                    <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="bookings-view-toggle">
                      <button
                        type="button"
                        onClick={() => setBookingsView("table")}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1 transition-colors ${
                          bookingsView === "table"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="bookings-view-table-btn"
                      >
                        <ListIcon className="w-3.5 h-3.5" />
                        List
                      </button>
                      <button
                        type="button"
                        onClick={() => setBookingsView("kanban")}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md flex items-center gap-1 transition-colors ${
                          bookingsView === "kanban"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="bookings-view-kanban-btn"
                      >
                        <LayoutGrid className="w-3.5 h-3.5" />
                        Kanban
                      </button>
                    </div>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search bookings..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {bookingsView === "kanban" ? (
                  <BookingsKanban
                    bookings={filteredBookings}
                    onMove={(id, status) => updateBookingStatus(id, status)}
                    crew={crew}
                    onAssignCrew={assignCrew}
                  />
                ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Service</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Location</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Date/Time</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Crew</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((booking) => (
                        <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-900">{booking.customer_name}</p>
                              <p className="text-xs text-slate-500">{booking.customer_email}</p>
                              <p className="text-xs text-slate-500">{booking.customer_phone}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium text-slate-900">{booking.service_name}</p>
                            <p className="text-xs text-slate-500 max-w-xs truncate">{booking.address}</p>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-slate-400 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-sm text-slate-900">{booking.community || "N/A"}</p>
                                <p className="text-xs text-slate-500">{booking.island || "Saint Lucia"}</p>
                              </div>
                            </div>
                            {booking.pinned_location && (
                              <div className="mt-2">
                                <LocationThumb
                                  lat={booking.pinned_location.lat}
                                  lng={booking.pinned_location.lng}
                                  size={104}
                                />
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-slate-900">{booking.preferred_date}</p>
                            <p className="text-xs text-slate-500">{booking.preferred_time}</p>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Select
                              value={booking.crew_id || "_unassigned"}
                              onValueChange={(val) =>
                                assignCrew(booking.id, val === "_unassigned" ? null : val)
                              }
                            >
                              <SelectTrigger
                                className="w-36 h-8 text-xs"
                                data-testid={`crew-assign-${booking.id}`}
                              >
                                <SelectValue placeholder="Unassigned" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_unassigned">Unassigned</SelectItem>
                                {crew
                                  .filter((c) => c.is_active !== false)
                                  .map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4">
                            {booking.is_recurring ? (
                              <Badge className="bg-purple-100 text-purple-700">
                                <RefreshCw className="w-3 h-3 mr-1" />
                                {booking.recurring_frequency}
                              </Badge>
                            ) : (
                              <span className="text-slate-500 text-sm">One-time</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Select
                                value={booking.status}
                                onValueChange={(val) => updateBookingStatus(booking.id, val)}
                              >
                                <SelectTrigger className="w-32 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteBooking(booking.id)}
                                className="text-red-600 hover:bg-red-50 h-8 px-2"
                                title="Delete booking"
                                data-testid={`delete-booking-${booking.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredBookings.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No bookings found</p>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Custom Requests Tab */}
          <TabsContent value="custom-requests">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-orange-500" />
                  Custom Service Requests
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Bookings submitted under the "Other" service category. Review the customer's
                  description, then create a priced quote.
                </p>
              </CardHeader>
              <CardContent>
                {(() => {
                  const customRequests = bookings.filter(
                    (b) => b.service_id === "other_custom"
                  );
                  if (customRequests.length === 0) {
                    return (
                      <p
                        className="text-center py-8 text-slate-500"
                        data-testid="custom-requests-empty"
                      >
                        No custom service requests yet.
                      </p>
                    );
                  }
                  return (
                    <div className="space-y-3" data-testid="custom-requests-list">
                      {customRequests.map((b) => (
                        <div
                          key={b.id}
                          className="p-4 rounded-lg border border-slate-200"
                          data-testid={`custom-request-${b.id}`}
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <p className="font-semibold text-slate-900">
                                  {b.customer_name}
                                </p>
                                <Badge className={getStatusColor(b.status)}>
                                  {b.status}
                                </Badge>
                                {b.is_recurring && (
                                  <Badge className="bg-purple-100 text-purple-700">
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    {b.recurring_frequency}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                {b.customer_email} • {b.customer_phone}
                              </p>
                              <p className="text-sm text-slate-500">
                                <MapPin className="w-3 h-3 inline mr-1" />
                                {[b.community, b.island].filter(Boolean).join(", ")} •{" "}
                                {b.preferred_date} {b.preferred_time}
                              </p>
                              <div className="mt-3 p-3 bg-orange-50 border border-orange-100 rounded-lg">
                                <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-1">
                                  Customer's request
                                </p>
                                <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid={`custom-request-desc-${b.id}`}>
                                  {b.custom_service_description ||
                                    b.notes ||
                                    "(no description provided)"}
                                </p>
                                {Array.isArray(b.photo_urls) && b.photo_urls.length > 0 && (
                                  <div className="flex gap-2 mt-3" data-testid={`custom-request-photos-${b.id}`}>
                                    {b.photo_urls.map((url, i) => (
                                      <a
                                        key={i}
                                        href={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-16 h-16 rounded-md overflow-hidden border border-slate-200 hover:ring-2 ring-orange-300"
                                      >
                                        <img
                                          src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                          alt="Customer photo"
                                          className="w-full h-full object-cover"
                                        />
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-2">
                                Submitted {new Date(b.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2 md:items-end">
                              <Button
                                size="sm"
                                className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
                                onClick={() => startQuoteForBooking(b)}
                                data-testid={`create-quote-btn-${b.id}`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Create Quote
                              </Button>
                              <Select
                                value={b.status}
                                onValueChange={(val) => updateBookingStatus(b.id, val)}
                              >
                                <SelectTrigger
                                  className="w-36 h-9 text-xs"
                                  data-testid={`custom-request-status-${b.id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card className="bg-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Quotes & Invoices</CardTitle>
                  <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                    <button
                      onClick={() => setInvoiceFilter("all")}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        invoiceFilter === "all" 
                          ? "bg-white text-slate-900 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      All ({invoices.length})
                    </button>
                    <button
                      onClick={() => setInvoiceFilter("pending")}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        invoiceFilter === "pending" 
                          ? "bg-white text-yellow-700 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      Pending ({invoices.filter(i => i.status === "pending").length})
                    </button>
                    <button
                      onClick={() => setInvoiceFilter("paid")}
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        invoiceFilter === "paid" 
                          ? "bg-white text-green-700 shadow-sm" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      Paid ({invoices.filter(i => i.status === "paid").length})
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Number</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Description</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Due Date</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices
                        .filter(invoice => {
                          if (invoiceFilter === "all") return true;
                          if (invoiceFilter === "pending") return invoice.status === "pending";
                          if (invoiceFilter === "paid") return invoice.status === "paid";
                          return true;
                        })
                        .map((invoice) => (
                        <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <Badge className={invoice.document_type === "quote" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}>
                              {invoice.document_type === "quote" ? "Quote" : "Invoice"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 font-mono font-medium text-slate-900">
                            {invoice.invoice_number}
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-slate-900">{invoice.user_name || "N/A"}</p>
                            <p className="text-xs text-slate-500">{invoice.user_email}</p>
                          </td>
                          <td className="py-3 px-4 text-slate-700 max-w-xs truncate">
                            {invoice.description}
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-semibold text-slate-900">XCD ${invoice.amount?.toFixed(2)}</p>
                            {invoice.amount_paid > 0 && invoice.document_type !== "quote" && (
                              <p className="text-xs text-green-600">Paid: ${invoice.amount_paid?.toFixed(2)}</p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-600">
                            {invoice.due_date}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={getStatusColor(invoice.status)}>
                              {invoice.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewInvoice(invoice)}
                                className="text-slate-600 hover:text-slate-900"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendEmail(invoice.invoice_number)}
                                className="text-green-600 hover:text-green-800"
                                title="Send via Email"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              {invoice.document_type === "quote" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleConvertToInvoice(invoice.invoice_number)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Convert to Invoice"
                                >
                                  <ArrowRightLeft className="w-4 h-4" />
                                </Button>
                              )}
                              {invoice.document_type === "invoice" &&
                                invoice.status !== "paid" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openMarkPaidDialog(invoice)}
                                    className="text-emerald-600 hover:text-emerald-800"
                                    title="Mark as Paid (record cash, cheque, transfer)"
                                    data-testid={`mark-paid-btn-${invoice.invoice_number}`}
                                  >
                                    <DollarSign className="w-4 h-4" />
                                  </Button>
                                )}
                              {invoice.document_type === "invoice" &&
                                !paidOutInvoiceSet.has(invoice.invoice_number) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openPayoutDialog(invoice)}
                                    className="text-indigo-600 hover:text-indigo-800"
                                    title="Pay crew for this invoice"
                                    data-testid={`pay-crew-btn-${invoice.invoice_number}`}
                                  >
                                    <Users2 className="w-4 h-4" />
                                  </Button>
                                )}
                              {paidOutInvoiceSet.has(invoice.invoice_number) && (
                                <Badge
                                  className="bg-indigo-100 text-indigo-700 ml-1"
                                  title="Crew has been paid for this invoice"
                                >
                                  Crew paid
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteInvoiceDoc(invoice.invoice_number)}
                                className="text-red-600 hover:bg-red-50"
                                title="Delete permanently"
                                data-testid={`delete-invoice-${invoice.invoice_number}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {invoices.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No quotes or invoices yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Registered Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Phone</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Joined</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-slate-900">{user.name}</p>
                            {user.company_name && (
                              <p className="text-xs text-slate-500">{user.company_name}</p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-700">{user.email}</td>
                          <td className="py-3 px-4 text-slate-700">{user.phone}</td>
                          <td className="py-3 px-4">
                            <Badge className={user.customer_type === "commercial" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                              {user.customer_type === "commercial" ? (
                                <><Building2 className="w-3 h-3 mr-1" /> Commercial</>
                              ) : (
                                <><Home className="w-3 h-3 mr-1" /> Residential</>
                              )}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteCustomer(user)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-customer-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No customers yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Contact Inquiries</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-semibold text-slate-900">{contact.name}</p>
                            <Badge className={getStatusColor(contact.status)}>{contact.status}</Badge>
                            <Badge className={contact.customer_type === "commercial" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                              {contact.customer_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">{contact.email} • {contact.phone}</p>
                          <p className="text-sm font-medium text-orange-600 mt-1">Service: {contact.service_type}</p>
                          <p className="text-slate-700 mt-2">{contact.message}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <p className="text-xs text-slate-500">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteContact(contact.id)}
                            className="text-red-600 hover:bg-red-50 h-8 px-2"
                            title="Delete inquiry"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {contacts.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No contact inquiries yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          {/* Portfolio Tab */}
          <TabsContent value="portfolio">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Project Portfolio</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Upload before/after photos of completed jobs. They appear on the public Portfolio page.
                </p>
              </CardHeader>
              <CardContent>
                {/* Upload form */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 mb-6 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="md:col-span-3">
                    <Label className="text-sm">Service Category</Label>
                    <Select value={portfolioCategory} onValueChange={setPortfolioCategory}>
                      <SelectTrigger data-testid="portfolio-upload-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="roofing">Roofing</SelectItem>
                        <SelectItem value="tiling">Tiling</SelectItem>
                        <SelectItem value="plumbing">Plumbing</SelectItem>
                        <SelectItem value="electrical">Electrical</SelectItem>
                        <SelectItem value="carpentry">Carpentry</SelectItem>
                        <SelectItem value="painting">Painting</SelectItem>
                        <SelectItem value="remodeling">Remodeling</SelectItem>
                        <SelectItem value="cleaning">Cleaning</SelectItem>
                        <SelectItem value="lawn_care">Lawn Care</SelectItem>
                        <SelectItem value="hvac">HVAC</SelectItem>
                        <SelectItem value="pest_control">Pest Control</SelectItem>
                        <SelectItem value="general_care">General Care</SelectItem>
                        <SelectItem value="metalwork">Metalwork & Welding</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-sm">Before Image</Label>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => setPortfolioBefore(e.target.files?.[0] || null)}
                      data-testid="portfolio-upload-before"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-sm">After Image</Label>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => setPortfolioAfter(e.target.files?.[0] || null)}
                      data-testid="portfolio-upload-after"
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button
                      type="button"
                      onClick={uploadPortfolioProject}
                      disabled={uploadingPortfolio}
                      className="bg-orange-500 hover:bg-orange-600 text-white w-full"
                      data-testid="portfolio-upload-submit"
                    >
                      {uploadingPortfolio ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Grid of existing projects */}
                {portfolio.length === 0 ? (
                  <p className="text-center py-8 text-slate-500" data-testid="portfolio-admin-empty">
                    No portfolio projects yet. Upload your first before/after pair above.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-admin-grid">
                    {portfolio.map((project) => (
                      <div
                        key={project.id}
                        className="rounded-lg overflow-hidden border border-slate-200 bg-white"
                        data-testid={`portfolio-admin-item-${project.id}`}
                      >
                        <div className="grid grid-cols-2 gap-0.5 bg-slate-100">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${project.before_url}`}
                            alt="Before"
                            className="w-full h-32 object-cover"
                          />
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${project.after_url}`}
                            alt="After"
                            className="w-full h-32 object-cover"
                          />
                        </div>
                        <div className="p-3 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-orange-100 text-orange-700 capitalize">
                              {project.service_category.replace("_", " ")}
                            </Badge>
                            {project.is_featured && (
                              <Badge className="bg-yellow-100 text-yellow-700">
                                <Star className="w-3 h-3 mr-1 fill-yellow-500 text-yellow-500" />
                                Featured
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => togglePortfolioFeatured(project.id)}
                              className={
                                project.is_featured
                                  ? "text-yellow-600 hover:bg-yellow-50"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                              data-testid={`portfolio-admin-feature-${project.id}`}
                              title={project.is_featured ? "Unfeature" : "Feature on homepage"}
                            >
                              <Star
                                className={`w-4 h-4 ${
                                  project.is_featured
                                    ? "fill-yellow-500 text-yellow-500"
                                    : ""
                                }`}
                              />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => deletePortfolioProject(project.id)}
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`portfolio-admin-delete-${project.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Free Quote Requests</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Leads submitted via the /quote page. Reach out promptly to maximise conversion.
                </p>
              </CardHeader>
              <CardContent>
                {quoteRequests.length === 0 ? (
                  <p className="text-center py-8 text-slate-500" data-testid="quotes-empty">
                    No quote requests yet.
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="admin-quotes-list">
                    {quoteRequests.map((q) => (
                      <div
                        key={q.id}
                        className="p-4 rounded-lg border border-slate-200"
                        data-testid={`admin-quote-${q.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-slate-900">{q.name}</p>
                              <Badge className="bg-slate-100 text-slate-700 capitalize">
                                {q.service_category.replace("_", " ")}
                              </Badge>
                              <Badge className="bg-blue-100 text-blue-700 capitalize">
                                {q.customer_type}
                              </Badge>
                              <Badge
                                className={
                                  q.status === "new"
                                    ? "bg-orange-100 text-orange-700"
                                    : q.status === "won"
                                    ? "bg-green-100 text-green-700"
                                    : q.status === "lost"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-slate-100 text-slate-700"
                                }
                              >
                                {q.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600">
                              {q.email} • {q.phone}
                            </p>
                            <p className="text-sm text-slate-500">
                              {[q.community, q.island].filter(Boolean).join(", ")}
                              {q.preferred_date && ` • Preferred: ${q.preferred_date}`}
                              {q.budget_range && ` • Budget: ${q.budget_range}`}
                            </p>
                            <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">
                              {q.details}
                            </p>
                            {Array.isArray(q.photo_urls) && q.photo_urls.length > 0 && (
                              <div className="flex gap-2 mt-3" data-testid={`admin-quote-photos-${q.id}`}>
                                {q.photo_urls.map((url, i) => (
                                  <a
                                    key={i}
                                    href={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-16 h-16 rounded-md overflow-hidden border border-slate-200 hover:ring-2 ring-orange-300"
                                  >
                                    <img
                                      src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                      alt="Quote photo"
                                      className="w-full h-full object-cover"
                                    />
                                  </a>
                                ))}
                              </div>
                            )}
                            {q.pinned_location && (
                              <div className="mt-3" data-testid={`admin-quote-location-${q.id}`}>
                                <p className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                                  <MapPin className="w-3 h-3 text-orange-500" /> Pinned location
                                </p>
                                <LocationThumb
                                  lat={q.pinned_location.lat}
                                  lng={q.pinned_location.lng}
                                  size={128}
                                />
                              </div>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(q.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex flex-col items-stretch gap-2">
                            <Select
                              value={q.status}
                              onValueChange={(v) => updateQuoteStatus(q.id, v)}
                            >
                              <SelectTrigger
                                className="w-36"
                                data-testid={`quote-status-select-${q.id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="quoted">Quoted</SelectItem>
                                <SelectItem value="won">Won</SelectItem>
                                <SelectItem value="lost">Lost</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteQuoteRequest(q.id)}
                              className="text-red-600 hover:bg-red-50 w-36 justify-center"
                              title="Delete quote request"
                              data-testid={`delete-quote-${q.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Customer Reviews</CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Approve reviews to make them visible publicly. Customers can only submit
                  reviews on completed bookings.
                </p>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <p className="text-center py-8 text-slate-500" data-testid="reviews-admin-empty">
                    No reviews yet.
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="admin-reviews-list">
                    {reviews.map((r) => (
                      <div
                        key={r.id}
                        className="p-4 rounded-lg border border-slate-200"
                        data-testid={`admin-review-${r.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-semibold text-slate-900">
                                {r.customer_name}
                              </p>
                              <div className="flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${
                                      i < r.rating
                                        ? "fill-orange-400 text-orange-400"
                                        : "text-slate-300"
                                    }`}
                                  />
                                ))}
                              </div>
                              <Badge
                                className={
                                  r.is_approved
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }
                              >
                                {r.is_approved ? "Approved" : "Pending"}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">
                              {r.comment}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                              {r.service_name} • {r.customer_location} •{" "}
                              {new Date(r.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!r.is_approved ? (
                              <Button
                                size="sm"
                                onClick={() => approveReview(r.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-review-${r.id}`}
                              >
                                <ThumbsUp className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rejectReview(r.id)}
                                data-testid={`unapprove-review-${r.id}`}
                              >
                                <ThumbsDown className="w-4 h-4 mr-1" />
                                Unapprove
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteReview(r.id)}
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`delete-review-${r.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Crew Tab */}
          <TabsContent value="crew">
            <Card className="bg-white">              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users2 className="w-5 h-5 text-orange-500" />
                      Crew & Technicians
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      Add your team members and assign them to bookings from the Bookings tab.
                    </p>
                  </div>
                  <Button
                    onClick={() => openCrewDialog(null)}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="add-crew-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Crew Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {crew.length === 0 ? (
                  <p
                    className="text-center py-8 text-slate-500"
                    data-testid="crew-empty"
                  >
                    No crew members yet. Add your first technician to start assigning bookings.
                  </p>
                ) : (
                  <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                    data-testid="crew-list"
                  >
                    {crew.map((c) => (
                      <div
                        key={c.id}
                        className="p-4 rounded-lg border border-slate-200 bg-white"
                        data-testid={`crew-card-${c.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
                              {c.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{c.name}</p>
                              <Badge
                                className={
                                  c.is_active
                                    ? "bg-green-100 text-green-700"
                                    : "bg-slate-100 text-slate-500"
                                }
                              >
                                {c.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {(c.specialties || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(c.specialties || []).map((s, i) => (
                              <Badge
                                key={i}
                                className="bg-blue-50 text-blue-700 text-xs"
                              >
                                {s}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {c.phone && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <PhoneIcon className="w-3 h-3" />
                            {c.phone}
                          </p>
                        )}
                        {c.email && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </p>
                        )}
                        {c.bio && (
                          <p className="text-xs text-slate-600 mt-2 italic">{c.bio}</p>
                        )}
                        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCrewDialog(c)}
                            data-testid={`edit-crew-${c.id}`}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteCrew(c.id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-crew-${c.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Crew Payouts Subsection */}
            <div className="mt-10 pt-6 border-t border-slate-200 space-y-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-indigo-500" />
                    Crew Payouts
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Track who you've paid for completed jobs. Outstanding payouts are
                    grouped by crew member for easy batching.
                  </p>
                </div>
                <Button
                  onClick={() => openPayoutDialog(null)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="record-payout-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record Payout
                </Button>
              </div>

              {/* Per-crew summary stats */}
              <Card className="bg-white">
                <CardContent className="pt-6">
                  {outstandingPayouts.length === 0 ? (
                    <p
                      className="text-center py-6 text-sm text-slate-500"
                      data-testid="payouts-no-outstanding"
                    >
                      Nothing owed right now — all completed jobs with assigned crew have been paid out.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {outstandingPayouts.map((g) => {
                        const totalOutstanding = g.invoices.reduce(
                          (acc, i) => acc + i.amount,
                          0
                        );
                        return (
                          <div
                            key={g.crew_id}
                            className="p-4 rounded-lg border border-indigo-200 bg-indigo-50"
                            data-testid={`outstanding-group-${g.crew_id}`}
                          >
                            <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                              {g.crew_name}
                            </p>
                            <p className="text-2xl font-bold text-slate-900 mt-1">
                              {g.invoices.length}{" "}
                              <span className="text-sm font-medium text-slate-500">
                                invoice{g.invoices.length === 1 ? "" : "s"}
                              </span>
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              Customer revenue: XCD ${totalOutstanding.toFixed(2)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Outstanding invoices list */}
              {outstandingPayouts.length > 0 && (
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="text-base">Awaiting Payout</CardTitle>
                    <p className="text-xs text-slate-500">
                      Invoices completed by an assigned crew member with no recorded payout yet.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" data-testid="outstanding-invoices-list">
                      {outstandingPayouts.map((g) => (
                        <div
                          key={g.crew_id}
                          className="border border-slate-200 rounded-lg overflow-hidden"
                        >
                          <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
                            <p className="text-sm font-semibold text-slate-900">
                              {g.crew_name}{" "}
                              <span className="text-slate-500 font-normal">
                                · {g.invoices.length} job{g.invoices.length === 1 ? "" : "s"}
                              </span>
                            </p>
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => {
                                setPayoutForm({
                                  crew_id: g.crew_id,
                                  invoice_numbers: g.invoices.map((i) => i.invoice_number),
                                  amount: "",
                                  payment_method: "cash",
                                  reference: "",
                                  notes: `Batch payout for ${g.invoices.length} jobs`,
                                  paid_at: new Date().toISOString().slice(0, 10),
                                });
                                setShowPayoutDialog(true);
                              }}
                              data-testid={`batch-payout-btn-${g.crew_id}`}
                            >
                              Pay All
                            </Button>
                          </div>
                          <ul className="divide-y divide-slate-100">
                            {g.invoices.map((inv) => (
                              <li
                                key={inv.invoice_number}
                                className="px-4 py-2 flex items-center justify-between text-sm"
                              >
                                <div>
                                  <span className="font-medium text-slate-900">
                                    {inv.invoice_number}
                                  </span>
                                  <span className="text-slate-500 ml-2">
                                    · {inv.user_name || "—"}
                                  </span>
                                  <Badge
                                    className={`ml-2 ${
                                      inv.status === "paid"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-yellow-100 text-yellow-700"
                                    }`}
                                  >
                                    {inv.status}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-slate-600">
                                    XCD ${inv.amount.toFixed(2)}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      openPayoutDialog({
                                        invoice_number: inv.invoice_number,
                                        booking_id: inv.booking_id,
                                      })
                                    }
                                    data-testid={`pay-single-${inv.invoice_number}`}
                                  >
                                    Pay
                                  </Button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent payouts list */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-base">Recent Payouts</CardTitle>
                </CardHeader>
                <CardContent>
                  {crewPayouts.length === 0 ? (
                    <p
                      className="text-center py-6 text-sm text-slate-500"
                      data-testid="payouts-empty"
                    >
                      No payouts recorded yet.
                    </p>
                  ) : (
                    <div className="overflow-x-auto" data-testid="payouts-history-table">
                      <table className="w-full text-sm">
                        <thead className="border-b border-slate-200">
                          <tr className="text-left">
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600">Date</th>
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600">Crew</th>
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600">Invoices</th>
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600">Method</th>
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600">Amount</th>
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600">Recorded by</th>
                            <th className="py-2 px-3 text-xs font-semibold text-slate-600"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {crewPayouts.map((p) => (
                            <tr
                              key={p.id}
                              className="border-b border-slate-100 hover:bg-slate-50"
                              data-testid={`payout-row-${p.id}`}
                            >
                              <td className="py-2 px-3 text-slate-700">
                                {p.paid_at?.slice(0, 10)}
                              </td>
                              <td className="py-2 px-3 font-medium text-slate-900">
                                {p.crew_name}
                              </td>
                              <td className="py-2 px-3 text-slate-600">
                                {(p.invoice_numbers || []).join(", ") || "—"}
                              </td>
                              <td className="py-2 px-3">
                                <Badge className="bg-slate-100 text-slate-700 capitalize">
                                  {p.payment_method.replace("_", " ")}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 font-semibold text-emerald-600">
                                XCD ${p.amount.toFixed(2)}
                              </td>
                              <td className="py-2 px-3 text-xs text-slate-500">
                                {p.recorded_by}
                              </td>
                              <td className="py-2 px-3 text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deletePayout(p.id)}
                                  className="text-red-600 hover:bg-red-50"
                                  data-testid={`delete-payout-${p.id}`}
                                  title="Reverse this payout"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="registry">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                  <h2 className="text-lg font-bold text-slate-900">
                    Labour Registry Applications
                  </h2>
                  <ManualRegistryDialog API={API} onCreated={fetchAllData} />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500">
                        <th className="py-3 px-3">Name</th>
                        <th className="py-3 px-3">Trade</th>
                        <th className="py-3 px-3">Parish</th>
                        <th className="py-3 px-3">Phone</th>
                        <th className="py-3 px-3">Tier</th>
                        <th className="py-3 px-3">Payment</th>
                        <th className="py-3 px-3">Documents</th>
                        <th className="py-3 px-3">Date</th>
                        <th className="py-3 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registryApps.map((app) => (
                        <tr key={app.id} className="border-b border-slate-100" data-testid={`registry-row-${app.id}`}>
                          <td className="py-3 px-3 font-medium text-slate-900">{app.full_name}</td>
                          <td className="py-3 px-3">{app.primary_trade}</td>
                          <td className="py-3 px-3">{app.parish}</td>
                          <td className="py-3 px-3">{app.phone}</td>
                          <td className="py-3 px-3">
                            <Badge className="bg-slate-100 text-slate-700">{app.tier}</Badge>
                          </td>
                          <td className="py-3 px-3">
                            <Badge className={app.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                              {app.payment_status === "paid" ? "Paid" : "Pending"}
                            </Badge>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex gap-2">
                              {app.id_card_path ? (
                                <a href={`${API}/labour-registry/document/${app.id_card_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs" data-testid={`registry-idcard-${app.id}`}>ID Card</a>
                              ) : (
                                <span className="text-xs text-slate-400">No ID</span>
                              )}
                              {app.resume_path && (
                                <a href={`${API}/labour-registry/document/${app.resume_path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs" data-testid={`registry-resume-${app.id}`}>CV</a>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-slate-500 whitespace-nowrap">
                            {new Date(app.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-3">
                            <Button variant="ghost" size="sm" onClick={() => deleteRegistryApp(app.id)} className="text-red-600 hover:bg-red-50 h-8 px-2" title="Delete application" data-testid={`delete-registry-${app.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {registryApps.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No registry applications yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="promos">
            <PromoCodesTab API={API} />
          </TabsContent>
        </Tabs>

        {/* Crew Payout Dialog */}
        <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
          <DialogContent className="max-w-lg" data-testid="payout-dialog">
            <DialogHeader>
              <DialogTitle>Record Crew Payout</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Crew member *</Label>
                <Select
                  value={payoutForm.crew_id}
                  onValueChange={(v) => setPayoutForm({ ...payoutForm, crew_id: v })}
                >
                  <SelectTrigger data-testid="payout-crew">
                    <SelectValue placeholder="Pick a crew member" />
                  </SelectTrigger>
                  <SelectContent>
                    {crew.filter((c) => c.is_active !== false).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Invoice(s) this payout covers</Label>
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (!v) return;
                    setPayoutForm((prev) => {
                      if (prev.invoice_numbers.includes(v)) return prev;
                      return { ...prev, invoice_numbers: [...prev.invoice_numbers, v] };
                    });
                  }}
                >
                  <SelectTrigger data-testid="payout-invoice-picker">
                    <SelectValue placeholder="Add invoice numbers..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {invoices
                      .filter(
                        (i) =>
                          i.document_type === "invoice" &&
                          !paidOutInvoiceSet.has(i.invoice_number) &&
                          !payoutForm.invoice_numbers.includes(i.invoice_number)
                      )
                      .map((i) => {
                        const linkedBooking = bookings.find((b) => b.id === i.booking_id);
                        const crewBadge = linkedBooking?.crew_name
                          ? ` · ${linkedBooking.crew_name}`
                          : " · (no crew assigned)";
                        return (
                          <SelectItem key={i.invoice_number} value={i.invoice_number}>
                            {i.invoice_number} — {i.user_name || "Customer"}{crewBadge}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
                {payoutForm.invoice_numbers.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2" data-testid="payout-selected-invoices">
                    {payoutForm.invoice_numbers.map((n) => (
                      <Badge key={n} className="bg-indigo-100 text-indigo-700 flex items-center gap-1">
                        {n}
                        <button
                          type="button"
                          onClick={() =>
                            setPayoutForm((prev) => ({
                              ...prev,
                              invoice_numbers: prev.invoice_numbers.filter((x) => x !== n),
                            }))
                          }
                          className="ml-1 hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Amount paid (XCD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={payoutForm.amount}
                    onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })}
                    placeholder="e.g. 850.00"
                    data-testid="payout-amount"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Date paid</Label>
                  <Input
                    type="date"
                    value={payoutForm.paid_at}
                    onChange={(e) => setPayoutForm({ ...payoutForm, paid_at: e.target.value })}
                    data-testid="payout-date"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Payment method *</Label>
                <Select
                  value={payoutForm.payment_method}
                  onValueChange={(v) => setPayoutForm({ ...payoutForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="payout-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Reference (cheque #, txn ID, etc.)</Label>
                <Input
                  value={payoutForm.reference}
                  onChange={(e) => setPayoutForm({ ...payoutForm, reference: e.target.value })}
                  placeholder="optional"
                  data-testid="payout-reference"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Internal notes</Label>
                <Textarea
                  value={payoutForm.notes}
                  onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                  rows={2}
                  placeholder="e.g. 'Weekly payout for Marcus — 3 jobs'"
                  data-testid="payout-notes"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={submitPayout}
                  disabled={savingPayout}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="payout-submit"
                >
                  {savingPayout ? "Recording..." : "Record Payout"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* Mark as Paid Dialog */}
        <Dialog
          open={!!markPaidInvoice}
          onOpenChange={(open) => !open && setMarkPaidInvoice(null)}
        >
          <DialogContent className="max-w-md" data-testid="mark-paid-dialog">
            <DialogHeader>
              <DialogTitle>
                Record Payment — {markPaidInvoice?.invoice_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Total:</span>
                  <span className="font-semibold">
                    XCD ${(markPaidInvoice?.amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Already paid:</span>
                  <span>XCD ${(markPaidInvoice?.amount_paid || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t border-slate-200">
                  <span className="font-semibold text-slate-700">Outstanding:</span>
                  <span className="font-bold text-orange-600">
                    XCD $
                    {(
                      (markPaidInvoice?.amount || 0) -
                      (markPaidInvoice?.amount_paid || 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Payment method *</Label>
                <Select
                  value={markPaidForm.payment_method}
                  onValueChange={(v) =>
                    setMarkPaidForm({ ...markPaidForm, payment_method: v })
                  }
                >
                  <SelectTrigger data-testid="mark-paid-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="card_in_person">Card (in person)</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Amount received (XCD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={markPaidForm.amount}
                  onChange={(e) =>
                    setMarkPaidForm({ ...markPaidForm, amount: e.target.value })
                  }
                  data-testid="mark-paid-amount"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Defaults to the full outstanding balance. Enter a smaller value for a
                  partial payment.
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">
                  Reference (cheque #, bank txn ID, etc.)
                </Label>
                <Input
                  value={markPaidForm.reference}
                  onChange={(e) =>
                    setMarkPaidForm({ ...markPaidForm, reference: e.target.value })
                  }
                  placeholder="optional"
                  data-testid="mark-paid-reference"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Internal notes</Label>
                <Textarea
                  value={markPaidForm.notes}
                  onChange={(e) =>
                    setMarkPaidForm({ ...markPaidForm, notes: e.target.value })
                  }
                  rows={2}
                  placeholder="e.g. 'paid to Marcus at job site'"
                  data-testid="mark-paid-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setMarkPaidInvoice(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={submitMarkPaid}
                  disabled={savingMarkPaid}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="mark-paid-submit"
                >
                  {savingMarkPaid ? "Recording..." : "Record Payment"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Crew Dialog */}
        <Dialog open={showCrewDialog} onOpenChange={setShowCrewDialog}>          <DialogContent className="max-w-md" data-testid="crew-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingCrewId ? "Edit Crew Member" : "Add Crew Member"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium">Full name *</Label>
                <Input
                  value={crewForm.name}
                  onChange={(e) => setCrewForm({ ...crewForm, name: e.target.value })}
                  placeholder="e.g. Marcus Joseph"
                  data-testid="crew-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Phone</Label>
                  <Input
                    value={crewForm.phone}
                    onChange={(e) => setCrewForm({ ...crewForm, phone: e.target.value })}
                    placeholder="+1 758..."
                    data-testid="crew-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Email</Label>
                  <Input
                    type="email"
                    value={crewForm.email}
                    onChange={(e) => setCrewForm({ ...crewForm, email: e.target.value })}
                    placeholder="optional"
                    data-testid="crew-email-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Specialties (comma-separated)</Label>
                <Input
                  value={crewForm.specialties}
                  onChange={(e) =>
                    setCrewForm({ ...crewForm, specialties: e.target.value })
                  }
                  placeholder="Roofing, Tiling, Plumbing"
                  data-testid="crew-specialties-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Short bio</Label>
                <Textarea
                  value={crewForm.bio}
                  onChange={(e) => setCrewForm({ ...crewForm, bio: e.target.value })}
                  rows={3}
                  placeholder="10+ years experience in residential roofing..."
                  data-testid="crew-bio-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="crew-active"
                  checked={crewForm.is_active}
                  onChange={(e) =>
                    setCrewForm({ ...crewForm, is_active: e.target.checked })
                  }
                  className="w-4 h-4"
                />
                <Label htmlFor="crew-active" className="text-sm font-medium cursor-pointer">
                  Active (available for assignment)
                </Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCrewDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={saveCrew}
                  disabled={savingCrew}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-crew-btn"
                >
                  {savingCrew ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Invoice Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden p-0">
            <DialogHeader className="p-4 border-b">
              <div className="flex items-center justify-between">
                <DialogTitle>
                  {previewInvoice?.document_type === "quote" ? "Quote" : "Invoice"} Preview
                </DialogTitle>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleSendEmail(previewInvoice?.invoice_number)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                  <Button
                    onClick={handleDownloadPDF}
                    disabled={downloadingPdf}
                    size="sm"
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingPdf ? "Generating..." : "Download PDF"}
                  </Button>
                  {previewInvoice?.document_type === "quote" && (
                    <Button
                      onClick={() => handleConvertToInvoice(previewInvoice?.invoice_number)}
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Convert to Invoice
                    </Button>
                  )}
                </div>
              </div>
            </DialogHeader>
            <div className="overflow-auto p-4" style={{ maxHeight: "calc(95vh - 80px)" }}>
              <div className="flex justify-center">
                <InvoiceTemplate ref={templateRef} invoice={previewInvoice} className="shadow-lg" />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
